import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  PipetteIcon,
  TrendingUp,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useFM } from '@/context/FMContext';
import { useFMPipeline, PIPELINE_STAGES, TERMINAL_STAGES } from '@/hooks/useFMPipeline';
import { axiosInstance } from '@/App';
import SwipeablePipelineItem from '@/components/fm/SwipeablePipelineItem';
import ProgressBarWithLabel from '@/components/fm/ProgressBarWithLabel';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency } from '@/lib/fmUtils';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { toast } from 'sonner';

const STAGE_LABELS = {
  trial_scheduled: 'Trial Scheduled',
  trial_done: 'Trial Done',
  follow_up: 'Follow-up',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

const STAGE_ICONS = {
  trial_scheduled: null,
  trial_done: null,
  follow_up: null,
  closed_won: CheckCircle2,
  closed_lost: XCircle,
};

const TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'fresh', label: 'Fresh PT' },
  { value: 'renewal', label: 'Renewals' },
];

/**
 * FMPipeline - Revenue pipeline with swipeable stage progression, quick add, and renewals-due.
 * Revenue progress bar sticky at top. Pipeline entries grouped by stage.
 */
export default function FMPipeline() {
  const { selectedGymId } = useFM();
  const [typeFilter, setTypeFilter] = useState('all');
  const [collapsedStages, setCollapsedStages] = useState({ closed_lost: true });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  // Trainers for quick add form
  const [trainers, setTrainers] = useState([]);

  // Fetch pipeline data
  const {
    byStage,
    summary,
    renewalsDue,
    loading,
    error,
    refetch,
    advanceEntry,
    markLost,
    createEntry,
  } = useFMPipeline(selectedGymId, typeFilter === 'all' ? null : typeFilter);

  // Fetch trainers for quick add form
  useEffect(() => {
    if (!selectedGymId) return;
    axiosInstance
      .get('/fm/trainers', { params: { gym_id: selectedGymId } })
      .then((res) => setTrainers(res.data.trainers || []))
      .catch(() => {});
  }, [selectedGymId]);

  const toggleStage = (stage) => {
    setCollapsedStages((prev) => ({ ...prev, [stage]: !prev[stage] }));
  };

  const handleEdit = (entry) => {
    setEditEntry(entry);
    // For now just show a toast; full edit can be added later
    toast.info(`Editing: ${entry.member_name}`);
  };

  // No gym selected
  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <TrendingUp className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">Select a gym to view pipeline</p>
      </div>
    );
  }

  // Non-terminal stages for display (active pipeline)
  const activeStages = PIPELINE_STAGES.filter((s) => !TERMINAL_STAGES.includes(s));
  const terminalStages = TERMINAL_STAGES;

  // Get target info from summary for progress bar
  // We fetch target progress separately for the split bar
  const wonValue = summary.won_value || 0;
  const totalPipelineValue = summary.total_pipeline_value || 0;

  return (
    <PullToRefresh onRefresh={refetch} pullingContent="" refreshingContent={<RefreshingSpinner />}>
      <div className="space-y-4 pb-20">
        {/* Page header */}
        <div>
          <h2 className="text-lg font-bold text-slate-800">Pipeline</h2>
          <p className="text-xs text-slate-500 mt-0.5">Manage leads and track revenue</p>
        </div>

        {/* 1. Revenue progress bar (sticky) */}
        <RevenueProgressBar gymId={selectedGymId} />

        {/* 2. Type tabs: All | Fresh PT | Renewals */}
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList className="w-full grid grid-cols-3">
            {TYPE_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="text-xs relative">
                {tab.label}
                {tab.value === 'renewal' && renewalsDue.total > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {renewalsDue.total > 9 ? '9+' : renewalsDue.total}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* 5. Renewals Due alert (shown in Renewals or All tab) */}
        {(typeFilter === 'renewal' || typeFilter === 'all') && renewalsDue.total > 0 && (
          <RenewalsDueAlert
            renewalsDue={renewalsDue}
            gymId={selectedGymId}
            onAddToPipeline={async (member) => {
              await createEntry({
                gym_id: selectedGymId,
                member_name: member.name,
                member_id: member.member_id,
                type: 'renewal',
                trainer_id: member.trainer_id || undefined,
                trial_date: undefined,
                value: 0,
              });
            }}
          />
        )}

        {/* Loading state */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border p-4 bg-white animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/3 mb-3" />
                <div className="h-3 bg-slate-100 rounded w-2/3 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={refetch} className="mt-2 text-xs text-red-600 underline">
              Retry
            </button>
          </div>
        )}

        {/* 3. Pipeline entries grouped by stage */}
        {!loading && !error && (
          <div className="space-y-3">
            {/* Active stages first */}
            {activeStages.map((stage) => {
              const stageEntries = byStage[stage] || [];
              if (stageEntries.length === 0 && TERMINAL_STAGES.includes(stage)) return null;
              const isCollapsed = collapsedStages[stage];

              return (
                <StageSection
                  key={stage}
                  stage={stage}
                  entries={stageEntries}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleStage(stage)}
                  onAdvance={advanceEntry}
                  onMarkLost={markLost}
                  onEdit={handleEdit}
                />
              );
            })}

            {/* Terminal stages */}
            {terminalStages.map((stage) => {
              const stageEntries = byStage[stage] || [];
              if (stageEntries.length === 0) return null;
              const isCollapsed = collapsedStages[stage] !== false; // collapsed by default for closed_lost

              return (
                <StageSection
                  key={stage}
                  stage={stage}
                  entries={stageEntries}
                  isCollapsed={stage === 'closed_lost' ? (collapsedStages[stage] !== false) : !!collapsedStages[stage]}
                  onToggle={() => toggleStage(stage)}
                  onAdvance={advanceEntry}
                  onMarkLost={markLost}
                  onEdit={handleEdit}
                  isTerminal
                />
              );
            })}

            {/* Empty state */}
            {Object.values(byStage).every((arr) => !arr || arr.length === 0) && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <TrendingUp className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-sm font-medium text-slate-700">No pipeline entries yet</p>
                <p className="text-xs text-slate-400 mt-1">Tap "Add Lead" to get started</p>
              </div>
            )}
          </div>
        )}

        {/* 4. Quick Add FAB */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="fixed bottom-20 right-4 z-40 bg-blue-600 text-white rounded-full px-5 py-3 shadow-lg flex items-center gap-2 active:scale-95 transition-transform"
        >
          <Plus className="w-5 h-5" />
          <span className="text-sm font-semibold">Add Lead</span>
        </button>

        {/* Quick Add Drawer */}
        <QuickAddDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          gymId={selectedGymId}
          trainers={trainers}
          onSubmit={createEntry}
        />
      </div>
    </PullToRefresh>
  );
}

/**
 * StageSection - collapsible section per pipeline stage.
 */
function StageSection({ stage, entries, isCollapsed, onToggle, onAdvance, onMarkLost, onEdit, isTerminal }) {
  const label = STAGE_LABELS[stage] || stage;
  const Icon = STAGE_ICONS[stage];
  const totalValue = entries.reduce((sum, e) => sum + (e.value || 0), 0);

  return (
    <div className={`rounded-xl border bg-white overflow-hidden ${isTerminal ? 'opacity-80' : ''}`}>
      {/* Stage header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 active:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className={`w-4 h-4 ${stage === 'closed_won' ? 'text-green-500' : 'text-red-400'}`} />}
          <span className="text-sm font-semibold text-slate-700 truncate">
            {label}
          </span>
          <span className="text-xs text-slate-500">
            ({entries.length})
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {totalValue > 0 && (
            <span className="text-xs font-medium text-slate-500">
              {formatCurrency(totalValue)}
            </span>
          )}
          {isCollapsed ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {/* Entries */}
      {!isCollapsed && entries.length > 0 && (
        <div>
          {entries.map((entry) => (
            <SwipeablePipelineItem
              key={entry.entry_id}
              entry={entry}
              onAdvance={onAdvance}
              onMarkLost={onMarkLost}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}

      {!isCollapsed && entries.length === 0 && (
        <div className="py-4 text-center text-xs text-slate-400">
          No entries in this stage
        </div>
      )}
    </div>
  );
}

/**
 * RevenueProgressBar - fetches target progress and shows split bar (fresh + renewal).
 */
function RevenueProgressBar({ gymId }) {
  const [progress, setProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    if (!gymId) return;
    setLoadingProgress(true);
    axiosInstance
      .get(`/fm/targets/${gymId}/progress`)
      .then((res) => setProgress(res.data))
      .catch(() => setProgress(null))
      .finally(() => setLoadingProgress(false));
  }, [gymId]);

  if (loadingProgress || !progress) {
    return (
      <div className="sticky top-0 z-10 bg-white rounded-lg border p-3 shadow-sm">
        <div className="h-4 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  const ptTarget = progress.pt_target || 0;
  const totalRevenue = progress.total_revenue || 0;
  const freshRevenue = progress.fresh_revenue || 0;
  const renewalRevenue = progress.renewal_revenue || 0;
  const pct = ptTarget > 0 ? Math.min((totalRevenue / ptTarget) * 100, 100) : 0;
  const freshPct = ptTarget > 0 ? Math.min((freshRevenue / ptTarget) * 100, 100) : 0;
  const renewalPct = ptTarget > 0 ? Math.min((renewalRevenue / ptTarget) * 100, 100) : 0;

  return (
    <div className="sticky top-0 z-10 bg-white rounded-lg border p-3 shadow-sm">
      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
        <span className="font-medium">
          {formatCurrency(totalRevenue)} / {formatCurrency(ptTarget)}
        </span>
        <span className={`font-bold ${pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
          {Math.round(pct)}%
        </span>
      </div>
      {/* Split progress bar: fresh (green) + renewal (purple) */}
      <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden flex">
        {freshPct > 0 && (
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${freshPct}%` }}
          />
        )}
        {renewalPct > 0 && (
          <div
            className="h-full bg-purple-500 transition-all duration-500"
            style={{ width: `${renewalPct}%` }}
          />
        )}
      </div>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
          Fresh {formatCurrency(freshRevenue)}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" />
          Renewal {formatCurrency(renewalRevenue)}
        </span>
      </div>
    </div>
  );
}

/**
 * RenewalsDueAlert - amber banner showing members with expiring PT.
 */
function RenewalsDueAlert({ renewalsDue, gymId, onAddToPipeline }) {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState({});

  const handleAdd = async (member) => {
    setAdding((prev) => ({ ...prev, [member.member_id]: true }));
    try {
      await onAddToPipeline(member);
    } catch {
      // Error handled in hook
    } finally {
      setAdding((prev) => ({ ...prev, [member.member_id]: false }));
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-800">
            {renewalsDue.total} member{renewalsDue.total !== 1 ? 's' : ''} with PT expiring in 14 days
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-amber-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-amber-400" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {renewalsDue.members.map((member) => (
            <div
              key={member.member_id}
              className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100"
            >
              <div>
                <p className="text-sm font-medium text-slate-700">{member.name}</p>
                <p className="text-xs text-slate-400">
                  Expires: {new Date(member.pt_expiry_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                  {member.trainer_name ? ` | ${member.trainer_name}` : ''}
                  {' | '}
                  {member.days_until_expiry === 0
                    ? 'Today'
                    : `${member.days_until_expiry} day${member.days_until_expiry !== 1 ? 's' : ''}`}
                </p>
              </div>
              <button
                onClick={() => handleAdd(member)}
                disabled={adding[member.member_id]}
                className="text-xs bg-amber-500 text-white px-3 py-1.5 rounded-lg font-medium active:scale-95 transition-transform disabled:opacity-50"
              >
                {adding[member.member_id] ? '...' : 'Add'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * QuickAddDrawer - vaul Drawer with form for adding new pipeline entries.
 */
function QuickAddDrawer({ open, onOpenChange, gymId, trainers, onSubmit }) {
  const [form, setForm] = useState({
    member_name: '',
    type: 'fresh',
    trainer_id: '',
    trial_date: '',
    value: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setForm({ member_name: '', type: 'fresh', trainer_id: '', trial_date: '', value: '' });
  };

  const handleSubmit = async () => {
    if (!form.member_name.trim()) {
      toast.error('Member name is required');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        gym_id: gymId,
        member_name: form.member_name.trim(),
        type: form.type,
        trainer_id: form.trainer_id || undefined,
        trial_date: form.trial_date || undefined,
        value: form.value ? parseFloat(form.value) : 0,
      });
      resetForm();
      onOpenChange(false);
    } catch {
      // Error handled in hook
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Add Lead</DrawerTitle>
          <DrawerDescription>Add a new pipeline entry</DrawerDescription>
        </DrawerHeader>

        <div className="px-4 space-y-4">
          {/* Member name */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Member Name *</label>
            <input
              type="text"
              value={form.member_name}
              onChange={(e) => setForm((f) => ({ ...f, member_name: e.target.value }))}
              placeholder="Enter member name"
              className="w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* Type toggle: Fresh / Renewal */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setForm((f) => ({ ...f, type: 'fresh' }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.type === 'fresh'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-slate-100 text-slate-500 border border-slate-200'
                }`}
              >
                Fresh PT
              </button>
              <button
                onClick={() => setForm((f) => ({ ...f, type: 'renewal' }))}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.type === 'renewal'
                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                    : 'bg-slate-100 text-slate-500 border border-slate-200'
                }`}
              >
                Renewal
              </button>
            </div>
          </div>

          {/* Trainer select */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Trainer</label>
            <Select
              value={form.trainer_id || 'none'}
              onValueChange={(val) => setForm((f) => ({ ...f, trainer_id: val === 'none' ? '' : val }))}
            >
              <SelectTrigger className="h-10 text-sm">
                <SelectValue placeholder="Select trainer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {trainers.map((t) => (
                  <SelectItem key={t.trainer_id} value={t.trainer_id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Trial date */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Trial Date</label>
            <input
              type="date"
              value={form.trial_date}
              onChange={(e) => setForm((f) => ({ ...f, trial_date: e.target.value }))}
              className="w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* Value (optional) */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Value (Rs)</label>
            <input
              type="number"
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              placeholder="0"
              min="0"
              className="w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
        </div>

        <DrawerFooter>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.member_name.trim()}
            className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {submitting ? 'Adding...' : 'Add to Pipeline'}
          </button>
          <DrawerClose asChild>
            <button className="w-full bg-slate-100 text-slate-600 py-3 rounded-lg text-sm font-medium">
              Cancel
            </button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Simple refreshing spinner for pull-to-refresh.
 */
function RefreshingSpinner() {
  return (
    <div className="flex justify-center py-3">
      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
